import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, Search } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from './ui/sheet';
import BrandLogo from './BrandLogo';
import SearchDialog from './SearchDialog';
import LanguageSwitcher from './LanguageSwitcher';
import { Button } from './ui-kit';
import { useContent } from '../i18n/LocaleProvider';
import { useScrollProgress } from '../lib/hooks';

const testId = (to) => `nav-${to === '/' ? 'home' : to.replace(/^\//, '').replace(/\//g, '-')}`;

export default function Header({ transparentOverHero = false, fracPublic = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const progress = useScrollProgress();
  const location = useLocation();
  const { PRIMARY_NAV, UI } = useContent();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const transparent = transparentOverHero && !scrolled;
  const nav = fracPublic ? [...PRIMARY_NAV, { label: 'Frac', to: '/frac' }] : PRIMARY_NAV;

  return (
    <header
      className={`site-header ${transparent ? 'site-header--transparent' : ''}`}
      data-testid="site-header"
    >
      <div className="u-container site-header__inner">
        <Link to="/" aria-label={UI.common.home} style={{ display: 'flex', alignItems: 'center' }}>
          <BrandLogo variant={transparent ? 'reversed' : 'light'} height={38} />
        </Link>

        <nav className="site-header__nav" aria-label="Primary">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className="site-nav__link"
              data-testid={testId(item.to)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            className="site-header__search"
            aria-label={UI.common.searchAria}
            onClick={() => setSearchOpen(true)}
            data-testid="header-search-trigger"
          >
            <Search size={18} aria-hidden="true" />
            <span className="site-header__search-hint" aria-hidden="true">
              ⌘K
            </span>
          </button>

          <LanguageSwitcher />

          <Button
            to="/investor-relations"
            variant={transparent ? 'secondary-on-navy' : 'primary'}
            className="site-header__cta"
            data-testid="header-ir-cta"
          >
            {UI.common.ir}
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="site-header__burger"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
                style={{
                  border: `1px solid ${transparent ? 'rgba(255,255,255,0.3)' : 'var(--border-default)'}`,
                  color: transparent ? '#fff' : 'var(--navy)',
                }}
              >
                <Menu size={20} aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              style={{
                background: 'var(--paper)',
                width: 'min(92vw, 380px)',
                overflowY: 'auto',
              }}
            >
              <SheetTitle className="sr-only">{UI.common.menu}</SheetTitle>
              <SheetDescription className="sr-only">{UI.common.menuNote}</SheetDescription>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 24,
                }}
              >
                <BrandLogo height={32} />
              </div>
              <nav aria-label="Primary (mobile)">
                {nav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className="mobile-nav__link"
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                ))}
                <button
                  type="button"
                  className="mobile-nav__link"
                  style={{ width: '100%', background: 'none', gap: 10 }}
                  onClick={() => {
                    setOpen(false);
                    setSearchOpen(true);
                  }}
                  data-testid="mobile-search-trigger"
                >
                  <Search size={18} aria-hidden="true" /> {UI.common.search}
                </button>
              </nav>

              <div style={{ marginTop: 24 }}>
                <p className="t-micro t-caption" style={{ marginBottom: 8 }}>
                  {UI.common.language}
                </p>
                <LanguageSwitcher inline />
              </div>

              <Button
                to="/investor-relations"
                variant="primary"
                className="w-full"
                style={{ marginTop: 24 }}
              >
                {UI.common.ir}
              </Button>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <span className="site-header__progress" style={{ width: `${progress * 100}%` }} aria-hidden="true" />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
